import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatLeagueName } from './utils'; // Importing from utils
import { fetchBoxScore, fetchNBAStandings, fetchPLStandings } from './api';
import './EventDetails.css';
import Tooltip from './components/Tooltip';

// League logo URLs - fallback logos if API doesn't provide them
const LEAGUE_LOGOS = {
    'NBA': 'https://cdn.nba.com/logos/nba/1610612737/primary/L/logo.svg',
    'PremierLeague': 'https://upload.wikimedia.org/wikipedia/en/f/f2/Premier_League_Logo.png'
};

// Function to get league logo URL
const getLeagueLogo = (league, leagueBadge) => {
    // First try to use the league badge from API
    if (leagueBadge && leagueBadge.trim() !== '') {
        return leagueBadge;
    }
    
    // Fallback to our predefined logos
    return LEAGUE_LOGOS[league] || '';
};

// Function to format league and status for compact display (same as GameList.js)
const formatLeagueAndStatus = (league, status) => {
    // Abbreviate league names
    let leagueAbbr = league;
    if (league === 'PremierLeague') {
        leagueAbbr = 'PL';
    } else if (league === 'NBA') {
        leagueAbbr = 'NBA';
    }
    
    // Abbreviate status
    let statusAbbr = status;
    if (status === 'Match Finished') {
        statusAbbr = 'FT';
    } else if (status === 'Full Time') {
        statusAbbr = 'FT';
    } else if (status === 'Half Time') {
        statusAbbr = 'HT';
    } else if (status === 'First Half') {
        statusAbbr = '1H';
    } else if (status === 'Second Half') {
        statusAbbr = '2H';
    } else if (status === 'Extra Time') {
        statusAbbr = 'ET';
    } else if (status === 'Penalties') {
        statusAbbr = 'PEN';
    }
    
    return `${leagueAbbr} • ${statusAbbr}`;
};

// Feature flag for box score functionality - set to false to hide box score
const ENABLE_BOX_SCORE = true;

const EventDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    // Helper function to get today's date safely
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    // Helper function to validate date string
    const isValidDate = (dateString) => {
        if (!dateString || dateString === '') return false;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime()) && dateString.match(/^\d{4}-\d{2}-\d{2}$/);
    };
    
    const urlDate = queryParams.get('date');
    const selectedDate = isValidDate(urlDate) ? urlDate : getTodayDate();
    const { eventStats, eventLineup, game, prediction } = location.state || {};

    const [videoError, setVideoError] = useState(false);
    const [isUpcoming, setIsUpcoming] = useState(false);
    const [isNBAGame, setIsNBAGame] = useState(false);
    const [isPLGame, setIsPLGame] = useState(false);
    const [boxScore, setBoxScore] = useState(null);
    const [boxScoreLoading, setBoxScoreLoading] = useState(false);
    const [boxScoreError, setBoxScoreError] = useState(null);
    const [standings, setStandings] = useState([]);
    const [standingsLoading, setStandingsLoading] = useState(false);
    const [standingsError, setStandingsError] = useState(null);
    const [selectedConference, setSelectedConference] = useState('ALL');
    const standingsSectionRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const standingsLoadedRef = useRef(false);
    const standingsRetryRef = useRef(0);

    // Define getYouTubeEmbedUrl at the top level so it's available everywhere
    const getYouTubeEmbedUrl = (url) => {
        if (!url) return null;
        const videoIdMatch = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/);
        return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}` : null;
    };

    // Helper function to format minutes properly
    const formatMinutes = (minutes) => {
        if (!minutes || minutes === '0' || minutes === 0) return '0:00';
        
        // Handle different minute formats from the API
        const minStr = minutes.toString();
        
        // If it's already in MM:SS format, return as is
        if (minStr.includes(':') && !minStr.includes('.')) {
            return minStr;
        }
        
        // If it contains decimals like "31.000000:10", extract the proper format
        if (minStr.includes('.') && minStr.includes(':')) {
            const parts = minStr.split('.');
            if (parts.length > 1) {
                const afterDecimal = parts[1];
                const colonIndex = afterDecimal.indexOf(':');
                if (colonIndex !== -1) {
                    const mins = parts[0];
                    const secs = afterDecimal.substring(colonIndex + 1);
                    return `${mins}:${secs.padStart(2, '0')}`;
                }
            }
        }
        
        // If it's a decimal number like 31.5, convert to minutes:seconds
        if (minStr.includes('.')) {
            const totalMinutes = parseFloat(minStr);
            const mins = Math.floor(totalMinutes);
            const secs = Math.round((totalMinutes - mins) * 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
        
        // If it's just a whole number, assume it's minutes
        return `${minStr}:00`;
    };

    // Helper functions for standings
    const getPerformanceTier = (team) => {
        if (isPLGame) {
            // Premier League tiers based on position
            if (team.position <= 4) return 'elite'; // UCL
            if (team.position <= 7) return 'strong'; // Europa
            if (team.position <= 14) return 'competitive'; // Mid-table
            if (team.position <= 17) return 'developing'; // Lower mid-table
            return 'rebuilding'; // Relegation zone
        } else {
            // NBA tiers based on playoff probability
            if (team.playoffProb >= 0.95) return 'elite';
            if (team.playoffProb >= 0.80) return 'strong';
            if (team.playoffProb >= 0.50) return 'competitive';
            if (team.playoffProb >= 0.20) return 'developing';
            return 'rebuilding';
        }
    };

    const getEloTrend = (change) => {
        if (change > 15) return 'hot';
        if (change > 0) return 'rising';
        if (change < -15) return 'cold';
        if (change < 0) return 'falling';
        return 'stable';
    };

    // Helper function to get shorter team names for mobile display
    const getShortTeamName = (fullName) => {
        if (!fullName) return '';
        
        // Common NBA team name mappings for mobile
        const teamNameMap = {
            'Boston Celtics': 'Celtics',
            'Milwaukee Bucks': 'Bucks',
            'Philadelphia 76ers': '76ers',
            'Cleveland Cavaliers': 'Cavaliers',
            'New York Knicks': 'Knicks',
            'Miami Heat': 'Heat',
            'Indiana Pacers': 'Pacers',
            'Orlando Magic': 'Magic',
            'Chicago Bulls': 'Bulls',
            'Atlanta Hawks': 'Hawks',
            'Brooklyn Nets': 'Nets',
            'Toronto Raptors': 'Raptors',
            'Charlotte Hornets': 'Hornets',
            'Washington Wizards': 'Wizards',
            'Detroit Pistons': 'Pistons',
            'Oklahoma City Thunder': 'Thunder',
            'Minnesota Timberwolves': 'Timberwolves',
            'Denver Nuggets': 'Nuggets',
            'Los Angeles Clippers': 'Clippers',
            'Phoenix Suns': 'Suns',
            'Sacramento Kings': 'Kings',
            'New Orleans Pelicans': 'Pelicans',
            'Los Angeles Lakers': 'Lakers',
            'Golden State Warriors': 'Warriors',
            'Dallas Mavericks': 'Mavericks',
            'Houston Rockets': 'Rockets',
            'Utah Jazz': 'Jazz',
            'Memphis Grizzlies': 'Grizzlies',
            'Portland Trail Blazers': 'Trail Blazers',
            'San Antonio Spurs': 'Spurs'
        };
        
        return teamNameMap[fullName] || fullName;
    };

    // Helper function to get three-letter city abbreviations for ultra-compact mobile display
    const getCityAbbreviation = (fullName) => {
        if (!fullName) return '';
        
        // Three-letter city abbreviations for NBA teams
        const cityAbbreviationMap = {
            'Boston Celtics': 'BOS',
            'Milwaukee Bucks': 'MIL',
            'Philadelphia 76ers': 'PHI',
            'Cleveland Cavaliers': 'CLE',
            'New York Knicks': 'NYK',
            'Miami Heat': 'MIA',
            'Indiana Pacers': 'IND',
            'Orlando Magic': 'ORL',
            'Chicago Bulls': 'CHI',
            'Atlanta Hawks': 'ATL',
            'Brooklyn Nets': 'BKN',
            'Toronto Raptors': 'TOR',
            'Charlotte Hornets': 'CHA',
            'Washington Wizards': 'WAS',
            'Detroit Pistons': 'DET',
            'Oklahoma City Thunder': 'OKC',
            'Minnesota Timberwolves': 'MIN',
            'Denver Nuggets': 'DEN',
            'Los Angeles Clippers': 'LAC',
            'LA Clippers': 'LAC',
            'Phoenix Suns': 'PHX',
            'Sacramento Kings': 'SAC',
            'New Orleans Pelicans': 'NOP',
            'Los Angeles Lakers': 'LAL',
            'LA Lakers': 'LAL',
            'Golden State Warriors': 'GSW',
            'Dallas Mavericks': 'DAL',
            'Houston Rockets': 'HOU',
            'Utah Jazz': 'UTA',
            'Memphis Grizzlies': 'MEM',
            'Portland Trail Blazers': 'POR',
            'San Antonio Spurs': 'SAS'
        };
        
        return cityAbbreviationMap[fullName] || fullName;
    };

    // Memoized filter handler to prevent unnecessary re-renders and scroll issues
    const handleConferenceFilter = useCallback((conference, e) => {
        // Prevent all default behaviors that might cause scrolling
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Save current scroll position before changing filter
        scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
        
        // Update the conference filter
        setSelectedConference(conference);
        
        // Restore scroll position after a short delay to allow for re-render
        setTimeout(() => {
            window.scrollTo(0, scrollPositionRef.current);
        }, 0);
    }, []);

    // Compact Standings Component (NBA or Premier League)
    const StandingsSection = () => {
        const leagueTitle = isPLGame ? "Premier League Rankings" : "NBA Power Rankings";
        const fullTableLink = isPLGame ? "/nba-standings?league=Premier+League" : "/nba-standings";
        
        if (standingsLoading) {
            return (
                <div className="standings-section">
                    <h2>{leagueTitle}</h2>
                    <div className="loading-message">Loading standings...</div>
                </div>
            );
        }

        if (standingsError) {
            return (
                <div className="standings-section">
                    <h2>{leagueTitle}</h2>
                    <div className="error-message">
                        Unable to load standings: {standingsError}
                    </div>
                </div>
            );
        }

        if (!standings || standings.length === 0) {
            return null;
        }

        // Filter and sort standings
        const filteredStandings = standings
            .filter(team => {
                if (isPLGame) {
                    return true; // No conference filtering for Premier League
                } else {
                    return selectedConference === 'ALL' || team.conference === selectedConference;
                }
            })
            .sort((a, b) => b.eloRating - a.eloRating)
            .slice(0, 10); // Show top 10 teams

        return (
            <div className="standings-section" ref={standingsSectionRef}>
                <div className="standings-header">
                    <div className="standings-title-section">
                        <h2>{leagueTitle}</h2>
                        <a href={fullTableLink} className="full-standings-link">
                            View Full Table →
                        </a>
                    </div>
                    {!isPLGame && (
                        <div className="conference-filters">
                        {['ALL', 'East', 'West'].map(conf => (
                            <div
                                key={conf}
                                role="button"
                                tabIndex={0}
                                className={`filter-btn ${selectedConference === conf ? 'active' : ''}`}
                                onClick={(e) => handleConferenceFilter(conf, e)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleConferenceFilter(conf, e);
                                    }
                                }}
                            >
                                {conf}
                            </div>
                        ))}
                        </div>
                    )}
                </div>
                
                <div className="standings-grid">
                    {filteredStandings.map((team, index) => {
                        const tier = getPerformanceTier(team);
                        const trend = getEloTrend(team.eloChange);
                        const rank = index + 1;

                        return (
                            <div key={team.team} className={`team-row ${tier} ${trend}`}>
                                <div className="team-info">
                                    <div className="rank-badge">#{rank}</div>
                                    <div className="team-details">
                                        <div className="team-name-row">
                                            <h3 className="team-name">
                                                {team.visuals?.logo && (
                                                    <img 
                                                        src={team.visuals.logo} 
                                                        alt=""
                                                        className="inline-logo"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <span className="team-name-full">{getShortTeamName(team.teamFullName)}</span>
                                                <span className="team-name-abbrev">{getCityAbbreviation(team.teamFullName)}</span>
                                            </h3>
                                        </div>
                                        <span className="team-meta">
                                            {isPLGame ? (
                                                <>Pos: {team.position} • {team.points} pts • GD: {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}</>
                                            ) : (
                                                <>{team.conference} • {team.currentRecord?.wins || 0}-{team.currentRecord?.losses || 0}</>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="elo-section">
                                    <div className="elo-rating">{team.eloRating}</div>
                                    <div className={`elo-change ${trend}`}>
                                        {team.eloChange > 0 ? '+' : ''}{team.eloChange}
                                    </div>
                                </div>

                                <div className="stats-section">
                                    {isPLGame ? (
                                        <>
                                            <div className="stat-group">
                                                <span className="stat-label">Recent Form</span>
                                                <div className="form-display">
                                                    {(team.form && team.form !== '' ? team.form : '-----').split('').slice(-3).map((result, idx) => (
                                                        <span key={idx} className={`form-dot ${result === 'W' ? 'form-win' : result === 'D' ? 'form-draw' : result === 'L' ? 'form-loss' : 'form-none'}`}>
                                                            {result === '-' ? '·' : result}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="stat-group">
                                                <span className="stat-label">Top 4 Odds</span>
                                                <div className="playoff-odds-container">
                                                    <span className="stat-value">{Math.round((team.top4Prob || 0) * 100)}%</span>
                                                    <div className="probability-bar">
                                                        <div 
                                                            className="probability-fill"
                                                            style={{
                                                                width: `${(team.top4Prob || 0) * 100}%`,
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="stat-group">
                                                <span className="stat-label">Point Diff</span>
                                                <span className={`stat-value ${team.pointDiff >= 0 ? 'positive' : 'negative'}`}>
                                                    {team.pointDiff > 0 ? '+' : ''}{team.pointDiff}
                                                </span>
                                            </div>
                                            <div className="stat-group">
                                                <span className="stat-label">Playoff Odds</span>
                                                <div className="playoff-odds-container">
                                                    <span className="stat-value">{Math.round((team.playoffProb || 0) * 100)}%</span>
                                                    <div className="probability-bar">
                                                        <div 
                                                            className="probability-fill"
                                                            style={{
                                                                width: `${(team.playoffProb || 0) * 100}%`,
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Performance Tier Legend */}
                <div className="tier-legend-compact">
                    <span className="legend-title">Tiers:</span>
                    <div className="legend-items">
                        <span className="legend-item">
                            <span className="legend-indicator elite"></span>
                            <span className="legend-label">Elite</span>
                            <span className="legend-range">(95%+)</span>
                        </span>
                        <span className="legend-item">
                            <span className="legend-indicator strong"></span>
                            <span className="legend-label">Strong</span>
                            <span className="legend-range">(80-94%)</span>
                        </span>
                        <span className="legend-item">
                            <span className="legend-indicator competitive"></span>
                            <span className="legend-label">Competitive</span>
                            <span className="legend-range">(50-79%)</span>
                        </span>
                        <span className="legend-item">
                            <span className="legend-indicator developing"></span>
                            <span className="legend-label">Developing</span>
                            <span className="legend-range">(20-49%)</span>
                        </span>
                        <span className="legend-item">
                            <span className="legend-indicator rebuilding"></span>
                            <span className="legend-label">Rebuilding</span>
                            <span className="legend-range">(&lt;20%)</span>
                        </span>
                    </div>
                    <div className="power-rating-explanation">
                        <span className="power-explanation-text">
                            {isPLGame 
                                ? "Power Rating: Team strength with 75% carryover from previous season (1500 = league average, 1400 = promoted teams)"
                                : "Power Rating: Team strength with 70% carryover from previous season (1500 = league average)"
                            }
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // Clean box score rendering component
    const BoxScoreSection = () => {
        if (boxScoreLoading) {
            return (
                <div className="box-score-section">
                    <h2>NBA Box Score</h2>
                    <div className="loading-message">Loading box score...</div>
                </div>
            );
        }

        if (boxScoreError) {
            // Don't render the section at all if there's an error (especially 404)
            return null;
        }

        if (!boxScore || !boxScore.box_score) {
            return null;
        }

        const { PlayerStats, TeamStats } = boxScore.box_score;

        if (!TeamStats || !PlayerStats) {
            return (
                <div className="box-score-section">
                    <h2>NBA Box Score</h2>
                    <div className="error-message">Box score data is incomplete</div>
                </div>
            );
        }

        // Check if the arrays are empty (no actual data)
        if (TeamStats.length === 0 || PlayerStats.length === 0) {
            return null; // Don't show the box score section at all
        }

        // Group players by team - properly identify home vs away
        // The API is called with fetchBoxScore(game.home_team, game.away_team, game.date)
        // So we need to match the team names to determine which is which
        let homeTeam, awayTeam;
        
        // Find teams by matching against the original game team names
        const team1 = TeamStats[0];
        const team2 = TeamStats[1];
        
        // Check if team1 name contains part of the home team name or vice versa
        const isTeam1Home = game.home_team.toLowerCase().includes(team1.TEAM_NAME.toLowerCase()) || 
                           team1.TEAM_NAME.toLowerCase().includes(game.home_team.toLowerCase());
        
        if (isTeam1Home) {
            homeTeam = team1;
            awayTeam = team2;
        } else {
            homeTeam = team2;
            awayTeam = team1;
        }
        
        const homePlayers = PlayerStats.filter(player => player.TEAM_ID === homeTeam.TEAM_ID);
        const awayPlayers = PlayerStats.filter(player => player.TEAM_ID === awayTeam.TEAM_ID);

        // Separate starters and bench players
        const getStarters = (players) => players.filter(player => player.START_POSITION && player.START_POSITION !== '');
        const getBench = (players) => players.filter(player => !player.START_POSITION || player.START_POSITION === '');

        // Helper function to render player stats in mobile-optimized order
        const renderPlayerStats = (player) => {
            return (
                <>
                    <span className="player-col">
                        <span className="player-name">{player.PLAYER_NAME}</span>
                        {player.START_POSITION && (
                            <span className="player-position">{player.START_POSITION}</span>
                        )}
                    </span>
                    <span>{formatMinutes(player.MIN)}</span>
                    <span className="points">{player.PTS}</span>
                    <span>{player.FGM}-{player.FGA}</span>
                    <span>{player.FG3M}-{player.FG3A}</span>
                    <span>{player.FTM}-{player.FTA}</span>
                    <span>{player.OREB}</span>
                    <span>{player.DREB}</span>
                    <span>{player.REB}</span>
                    <span>{player.AST}</span>
                    <span>{player.STL}</span>
                    <span>{player.BLK}</span>
                    <span>{player.TO}</span>
                    <span>{player.PF}</span>
                    <span>{player.PLUS_MINUS > 0 ? '+' : ''}{player.PLUS_MINUS}</span>
                </>
            );
        };

        // Helper function to render team totals in mobile-optimized order
        const renderTeamTotals = (teamStats) => {
            return (
                <>
                    <span className="player-col">
                        <span className="team-total-label">TEAM</span>
                    </span>
                    <span>{formatMinutes(teamStats.MIN)}</span>
                    <span className="points">{teamStats.PTS}</span>
                    <span>{teamStats.FGM}-{teamStats.FGA}</span>
                    <span>{teamStats.FG3M}-{teamStats.FG3A}</span>
                    <span>{teamStats.FTM}-{teamStats.FTA}</span>
                    <span>{teamStats.OREB}</span>
                    <span>{teamStats.DREB}</span>
                    <span>{teamStats.REB}</span>
                    <span>{teamStats.AST}</span>
                    <span>{teamStats.STL}</span>
                    <span>{teamStats.BLK}</span>
                    <span>{teamStats.TO}</span>
                    <span>{teamStats.PF}</span>
                    <span></span>
                </>
            );
        };

        const renderPlayerTable = (teamName, players, teamStats, teamBadge) => {
            const starters = getStarters(players);
            const bench = getBench(players);

            return (
                <div className="team-box-score">
                    <div className="team-header">
                        <span className="team">
                            {teamBadge && (
                                <img
                                    src={teamBadge}
                                    alt={`${teamName} Badge`}
                                    className="team-badge"
                                />
                            )}
                            <strong>{teamName}</strong>
                        </span>
                        {/* <div className="team-score">{teamStats.PTS}</div> */}
                    </div>
                    
                    <div className="player-stats-table">
                        {/* Mobile-optimized Table Header */}
                        <div className="stats-header">
                            <span className="player-col">PLAYER</span>
                            <span>MIN</span>
                            <span>PTS</span>
                            <span>FG</span>
                            <span>3PT</span>
                            <span>FT</span>
                            <span>OREB</span>
                            <span>DREB</span>
                            <span>REB</span>
                            <span>AST</span>
                            <span>STL</span>
                            <span>BLK</span>
                            <span>TO</span>
                            <span>PF</span>
                            <span>+/-</span>
                        </div>

                        {/* Starters Section */}
                        <div className="section-label">STARTERS</div>
                        {starters.map((player, index) => (
                            <div key={index} className="player-row">
                                {renderPlayerStats(player)}
                            </div>
                        ))}

                        {/* Bench Section */}
                        {bench.length > 0 && (
                            <>
                                <div className="section-label">BENCH</div>
                                {bench.map((player, index) => (
                                    <div key={index} className="player-row">
                                        {renderPlayerStats(player)}
                                    </div>
                                ))}
                            </>
                        )}

                        {/* Team Totals */}
                        <div className="team-totals">
                            {renderTeamTotals(teamStats)}
                        </div>

                        {/* Team Percentages - mobile optimized */}
                        <div className="team-percentages">
                            <span className="player-col"></span>
                            <span></span>
                            <span></span>
                            <span>{(teamStats.FG_PCT * 100).toFixed(1)}%</span>
                            <span>{(teamStats.FG3_PCT * 100).toFixed(1)}%</span>
                            <span>{(teamStats.FT_PCT * 100).toFixed(1)}%</span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div className="box-score-section">
                <h2>Box Score</h2>
                
                <div className="box-score-container">
                    {renderPlayerTable(awayTeam.TEAM_NAME, awayPlayers, awayTeam, game.away_team_badge)}
                    {renderPlayerTable(homeTeam.TEAM_NAME, homePlayers, homeTeam, game.home_team_badge)}
                </div>
            </div>
        );
    };

    // Scroll to the top of the page on navigation (only when game changes)
    useEffect(() => {
        // Only scroll to top on initial load or when switching games
        // Don't scroll if we're just changing conference filters
        if (game) {
            window.scrollTo(0, 0);
            
            // Check if this is an upcoming game
            if (game.status === 'Not Started' || game.status === 'NS') {
                setIsUpcoming(true);
            }

            // Check if this is an NBA game
            if (game.league === 'NBA') {
                setIsNBAGame(true);
                setIsPLGame(false);
            } else if (game.league && (
                game.league.includes('Premier League') || 
                game.league.includes('English Premier') ||
                game.league === 'EPL' ||
                game.league.includes('England Premier') ||
                game.league === 'PremierLeague' ||
                game.league.includes('PremierLeague')
            )) {
                setIsPLGame(true);
                setIsNBAGame(false);
            } else {
                setIsNBAGame(false);
                setIsPLGame(false);
            }
        }
    }, [game?.date, game?.home_team, game?.away_team]); // Only react to actual game changes

    // Load box score for NBA games
    useEffect(() => {
        const loadBoxScore = async () => {
            if (!ENABLE_BOX_SCORE || !isNBAGame || !game || isUpcoming) {
                return;
            }

            setBoxScoreLoading(true);
            setBoxScoreError(null);

            try {
                const boxScoreData = await fetchBoxScore(game.home_team, game.away_team, game.date);
                
                if (boxScoreData) {
                    setBoxScore(boxScoreData);
                } else {
                    setBoxScoreError('No box score data available');
                }
            } catch (error) {
                setBoxScoreError(error.message || 'Failed to load box score');
            } finally {
                setBoxScoreLoading(false);
            }
        };

        loadBoxScore();
    }, [isNBAGame, game, isUpcoming]);

    // Load standings (NBA or Premier League)
    useEffect(() => {
        const loadStandings = async function loadStandingsWithRetry() {
            if ((!isNBAGame && !isPLGame) || !game || standingsLoadedRef.current) {
                return;
            }
            
            // For NBA games, only show standings for completed games
            // For Premier League games, show standings for both upcoming and completed games
            if (isNBAGame && isUpcoming) {
                return;
            }

            standingsLoadedRef.current = true;
            setStandingsLoading(true);
            setStandingsError(null);

            try {
                const standingsData = isNBAGame 
                    ? await fetchNBAStandings() 
                    : await fetchPLStandings();

                if (standingsData) {
                    setStandings(standingsData);
                    standingsRetryRef.current = 0;
                } else {
                    throw new Error('No standings data available');
                }
            } catch (error) {
                setStandingsError(error.message || 'Failed to load standings');
                standingsLoadedRef.current = false; // allow retry on failure
                standingsRetryRef.current += 1;
                if (standingsRetryRef.current <= 2) {
                    setTimeout(loadStandingsWithRetry, 1000 * standingsRetryRef.current);
                }
            } finally {
                setStandingsLoading(false);
            }
        };

        loadStandings();
    }, [isNBAGame, isPLGame, game, isUpcoming]);

    // If we don't have any game data at all, show a generic message
    if (!game) {
        return (
            <div className="event-details">
                <h1 className="event-details-title">Event Details</h1>
                <div className="no-details">
                    <p>Event details are currently unavailable.</p>
                    <p>Stay tuned for updates!</p>
                </div>
            </div>
        );
    }

    // For upcoming games or games without event stats, show a specialized view
    if (isUpcoming || !eventStats || !eventStats.eventstats || eventStats.eventstats.length === 0) {
        return (
            <div className="event-details">
                <h1 className="event-details-title">{`${game.home_team} vs ${game.away_team}`}</h1>
                
                {/* Upcoming Game Banner */}
                {isUpcoming && (
                    <div className="upcoming-game-banner">
                        <span className="upcoming-label">Upcoming Match</span>
                        {game.date && (
                            <span className="game-date">
                                {new Date(game.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </span>
                        )}
                        {game.time && <span className="game-time">Kickoff: {game.time}</span>}
                        {game.venue && <span className="game-venue">Venue: {game.venue}</span>}
                    </div>
                )}



                {/* NBA Game Banner - show for completed NBA games */}
                {isNBAGame && !isUpcoming && (
                    <div className="nba-game-banner">
                        <span className="nba-label">Game Details</span>
                        {game.date && (
                            <span className="game-date">
                                {new Date(game.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </span>
                        )}
                        {game.status && <span className="game-status">Status: {game.status}</span>}
                        {game.venue && <span className="game-venue">Venue: {game.venue}</span>}
                    </div>
                )}

                {/* Centered Game Card */}
                <div className="centered-card">
                    <div className="game-card">
                        {/* League and Status Header */}
                        <div className="game-header">
                            <span className="league-status">
                                {(() => {
                                    const leagueLogo = getLeagueLogo(game.league, game.league_badge);
                                    return (
                                        <>
                                            {leagueLogo && leagueLogo.trim() !== '' && (
                                                <img 
                                                    src={leagueLogo} 
                                                    alt={`${formatLeagueName(game.league)} logo`}
                                                    className="league-logo"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            {formatLeagueAndStatus(game.league, game.status)}
                                        </>
                                    );
                                })()}
                            </span>
                        </div>
                        <div className="game-row">
                            <div className="team-info">
                                <img
                                    src={game.home_team_badge}
                                    alt={`${game.home_team} Badge`}
                                    className="team-badge"
                                />
                                <strong>{game.home_team}</strong>
                            </div>
                            {!isUpcoming && game.home_score !== undefined && (
                                <div className="score-column">
                                    <span className="team-score">
                                        {game.home_score}
                                    </span>
                                </div>
                            )}
                            {prediction && (
                                <div className="win-prob-container">
                                    <span className="prob-label">Win Prob.</span>
                                    <span
                                        className={`win-prob home ${
                                            prediction.home_win_prob > prediction.away_win_prob
                                                ? 'favored'
                                                : ''
                                        }`}
                                    >
                                        {prediction.home_win_prob !== undefined
                                            ? `${Math.round(prediction.home_win_prob * 100)}%`
                                            : 'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="game-divider"></div>
                        <div className="game-row">
                            <div className="team-info">
                                <img
                                    src={game.away_team_badge}
                                    alt={`${game.away_team} Badge`}
                                    className="team-badge"
                                />
                                <strong>{game.away_team}</strong>
                            </div>
                            {!isUpcoming && game.away_score !== undefined && (
                                <div className="score-column">
                                    <span className="team-score">
                                        {game.away_score}
                                    </span>
                                </div>
                            )}
                            {prediction && (
                                <div className="win-prob-container">
                                    <span
                                        className={`win-prob away ${
                                            prediction.away_win_prob > prediction.home_win_prob
                                                ? 'favored'
                                                : ''
                                        }`}
                                    >
                                        {prediction.away_win_prob !== undefined
                                            ? `${Math.round(prediction.away_win_prob * 100)}%`
                                            : 'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="game-details">
                            {prediction && game.league !== 'NBA' && (
                                <span className="draw-prob">
                                    Draw: {prediction.draw_prob !== undefined
                                        ? `${Math.round(prediction.draw_prob * 100)}%`
                                        : 'N/A'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Ask Rodman Button */}
                <div className="rodman-section">
                    <Tooltip 
                        content="Ask RODMAN AI to break down what happened in this game"
                        position="top"
                        delay={300}
                    >
                        <button 
                            className="ask-rodman-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Create enriched game context with event stats, prediction data, and box score
                                const enrichedGameContext = {
                                    ...game,
                                    prediction: prediction,
                                    eventStats: eventStats,
                                    eventLineup: eventLineup,
                                    boxScore: isNBAGame && !isUpcoming ? boxScore : null
                                };
                                
                                navigate('/rodman', { 
                                    state: { 
                                        gameContext: enrichedGameContext,
                                        gameString: `${game.home_team} vs ${game.away_team}`,
                                        league: game.league,
                                        presetQuery: isUpcoming 
                                            ? `Give me your pre-game breakdown for ${game.home_team} vs ${game.away_team}` 
                                            : `Analyze the ${game.home_team} vs ${game.away_team} game - what happened? They scored ${game.home_score}-${game.away_score}.`,
                                        autoStart: true
                                    }
                                });
                            }}
                        >
                            ⚡ Ask RODMAN
                        </button>
                    </Tooltip>
                </div>

                {/* YouTube Video Section - show for completed games */}
                {!isUpcoming && game.video && (
                    <div className="video-section">
                        {!videoError ? (
                            <div className="video-wrapper">
                                <iframe
                                    src={getYouTubeEmbedUrl(game.video)}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    onError={() => setVideoError(true)}
                                ></iframe>
                            </div>
                        ) : (
                            <div className="video-error">
                                <p>Unable to load video. <a href={game.video} target="_blank" rel="noopener noreferrer">Watch on YouTube</a></p>
                            </div>
                        )}
                    </div>
                )}

                {/* Game Preview Section */}
                <div className="game-preview">
                    <h2>{isUpcoming ? "Match Preview" : "Game Summary"}</h2>
                    <p>
                        {isUpcoming ? (
                            <>
                                This match between {game.home_team} and {game.away_team} is scheduled to take place
                                {game.venue ? ` at ${game.venue}` : ''}.
                                {prediction ? 
                                    ` Based on our predictions, ${
                                        prediction.home_win_prob > prediction.away_win_prob 
                                        ? game.home_team 
                                        : prediction.away_win_prob > prediction.home_win_prob 
                                            ? game.away_team 
                                            : "neither team"
                                    } has the edge in this matchup.` 
                                    : ''}
                            </>
                        ) : (
                            <>
                                {game.home_team} {game.home_score !== undefined ? game.home_score : '?'} - {game.away_score !== undefined ? game.away_score : '?'} {game.away_team}
                                {game.venue ? ` at ${game.venue}` : ''}.
                                {prediction ? 
                                    ` Our model predicted ${
                                        prediction.home_win_prob > prediction.away_win_prob 
                                        ? game.home_team 
                                        : prediction.away_win_prob > prediction.home_win_prob 
                                            ? game.away_team 
                                            : "a draw"
                                    } as the likely outcome.` 
                                    : ''}
                            </>
                        )}
                    </p>
                </div>

                {/* NBA Box Score Section - only for completed NBA games */}
                {ENABLE_BOX_SCORE && isNBAGame && !isUpcoming && <BoxScoreSection />}

                {/* Standings Section - for completed NBA games and all Premier League games (positioned last) */}
                {((isNBAGame && !isUpcoming) || isPLGame) && <StandingsSection />}
            </div>
        );
    }

    // Original code for completed games with stats (Premier League games)
    const possessionStat = eventStats.eventstats.find((stat) => stat.strStat === 'Ball Possession');
    const stats = eventStats.eventstats.filter((stat) => stat.strStat !== 'Ball Possession');

    const getWinnerClass = (value, opposingValue) => {
        const valueFloat = parseFloat(value);
        const opposingValueFloat = parseFloat(opposingValue);
        if (valueFloat > opposingValueFloat) return 'highlight';
        return ''; // No highlight for the losing team
    };

    const embedUrl = game.video ? getYouTubeEmbedUrl(game.video) : null;

    return (
        <div className="event-details">
            <h1 className="event-details-title">{`${game.home_team} vs ${game.away_team}`}</h1>

            {/* Centered Game Card */}
            <div className="centered-card">
                <div className="game-card">
                    {/* League and Status Header */}
                    <div className="game-header">
                        <span className="league-status">
                            {(() => {
                                const leagueLogo = getLeagueLogo(game.league, game.league_badge);
                                return (
                                    <>
                                        {leagueLogo && (
                                            <img 
                                                src={leagueLogo} 
                                                alt={`${formatLeagueName(game.league)} logo`}
                                                className="league-logo"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        {formatLeagueAndStatus(game.league, game.status)}
                                    </>
                                );
                            })()}
                        </span>
                    </div>
                    <div className="game-row">
                        <div className="team-info">
                            <img
                                src={game.home_team_badge}
                                alt={`${game.home_team} Badge`}
                                className="team-badge"
                            />
                            <strong>{game.home_team}</strong>
                        </div>
                        <div className="score-column">
                            <span className="team-score">
                                {game.home_score !== undefined ? game.home_score : '?'}
                            </span>
                        </div>
                        {prediction && (
                            <div className="win-prob-container">
                                <span className="prob-label">Win Prob.</span>
                                <span
                                    className={`win-prob home ${
                                        prediction.home_win_prob > prediction.away_win_prob
                                            ? 'favored'
                                            : ''
                                    }`}
                                >
                                    {prediction.home_win_prob !== undefined
                                        ? `${Math.round(prediction.home_win_prob * 100)}%`
                                        : 'N/A'}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="game-divider"></div>
                    <div className="game-row">
                        <div className="team-info">
                            <img
                                src={game.away_team_badge}
                                alt={`${game.away_team} Badge`}
                                className="team-badge"
                            />
                            <strong>{game.away_team}</strong>
                        </div>
                        <div className="score-column">
                            <span className="team-score">
                                {game.away_score !== undefined ? game.away_score : '?'}
                            </span>
                        </div>
                        {prediction && (
                            <div className="win-prob-container">
                                <span
                                    className={`win-prob away ${
                                        prediction.away_win_prob > prediction.home_win_prob
                                            ? 'favored'
                                            : ''
                                    }`}
                                >
                                    {prediction.away_win_prob !== undefined
                                        ? `${Math.round(prediction.away_win_prob * 100)}%`
                                        : 'N/A'}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="game-details">
                        {prediction && game.league !== 'NBA' && (
                            <span className="draw-prob">
                                Draw: {prediction.draw_prob !== undefined
                                    ? `${Math.round(prediction.draw_prob * 100)}%`
                                    : 'N/A'}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Ask Rodman Button */}
            <div className="rodman-section">
                <Tooltip 
                    content="Ask RODMAN AI to break down what happened in this game"
                    position="top"
                    delay={300}
                >
                    <button 
                        className="ask-rodman-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Create enriched game context with event stats, prediction data, and box score
                            const enrichedGameContext = {
                                ...game,
                                prediction: prediction,
                                eventStats: eventStats,
                                eventLineup: eventLineup,
                                boxScore: isNBAGame && !isUpcoming ? boxScore : null
                            };
                            
                            navigate('/rodman', { 
                                state: { 
                                    gameContext: enrichedGameContext,
                                    gameString: `${game.home_team} vs ${game.away_team}`,
                                    league: game.league,
                                    presetQuery: isUpcoming 
                                        ? `Give me your pre-game breakdown for ${game.home_team} vs ${game.away_team}` 
                                        : `Analyze the ${game.home_team} vs ${game.away_team} game - what happened? They scored ${game.home_score}-${game.away_score}.`,
                                    autoStart: true
                                }
                            });
                        }}
                    >
                        ⚡ Ask RODMAN
                    </button>
                </Tooltip>
            </div>

            {/* Video Section - show highlights early for all completed games */}
            {game.video && (
                <div className="video-section">
                    <h2 className="section-title">Highlights</h2>
                    {!videoError ? (
                        <div className="video-wrapper">
                            <iframe
                                src={getYouTubeEmbedUrl(game.video)}
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                onError={() => setVideoError(true)}
                            ></iframe>
                        </div>
                    ) : (
                        <div className="video-error">
                            <p>Unable to load video. <a href={game.video} target="_blank" rel="noopener noreferrer">Watch on YouTube</a></p>
                        </div>
                    )}
                </div>
            )}



            {/* NBA Box Score Section - only for NBA games */}
            {ENABLE_BOX_SCORE && isNBAGame && <BoxScoreSection />}

            {/* Game Summary Section - for completed football games */}
            <div className="game-preview">
                <h2>Game Summary</h2>
                <p>
                    {game.home_team} {game.home_score !== undefined ? game.home_score : '?'} - {game.away_score !== undefined ? game.away_score : '?'} {game.away_team}
                    {game.venue ? ` at ${game.venue}` : ''}.
                    {prediction ? 
                        ` Our model predicted ${
                            prediction.home_win_prob > prediction.away_win_prob 
                            ? game.home_team 
                            : prediction.away_win_prob > prediction.home_win_prob 
                                ? game.away_team 
                                : "a draw"
                        } as the likely outcome.` 
                        : ''}
                </p>
            </div>

            {/* Event Stats Section - for Premier League and other sports */}
            {eventStats && eventStats.eventstats && eventStats.eventstats.length > 0 && (
                <div className="stats-card">
                    {/* <h2>Match Statistics</h2> */}
                    {/* Possession Section */}
                    {possessionStat && (
                        <div className="possession-container">
                            <h2 className="possession-label">{possessionStat.strStat}</h2>
                            <div className="possession-row">
                                <div className="possession-bar">
                                    <div
                                        className="bar home"
                                        style={{ width: `${possessionStat.intHome}%` }}
                                    >
                                        <span className="possession-percentage">{possessionStat.intHome}%</span>
                                    </div>
                                    <div
                                        className="bar away"
                                        style={{ width: `${possessionStat.intAway}%` }}
                                    >
                                        <span className="possession-percentage">{possessionStat.intAway}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Other Stats */}
                    <div className="stats-list">
                        {stats.map((stat, index) => (
                            <div key={index} className="stat-row">
                                <div
                                    className={`team-value ${getWinnerClass(stat.intHome, stat.intAway)}`}
                                >
                                    {stat.intHome}
                                </div>
                                <div className="stat-name">{stat.strStat}</div>
                                <div
                                    className={`team-value away ${getWinnerClass(stat.intAway, stat.intHome)}`}
                                >
                                    {stat.intAway}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Lineup Section - Master version for non-NBA games */}
            {eventLineup && eventLineup.lineup && (
                <div className="lineups-container">
                    <div className="lineup-team">
                        <div className="lineup-header">
                            <img src={game.home_team_badge} alt={`${game.home_team} Badge`} className="team-badge" />
                            <h2>{game.home_team}</h2>
                        </div>
                        <div className="lineup-players">
                            {eventLineup.lineup
                                .filter(player => player.strTeam === game.home_team)
                                .map((player, index) => (
                                    <div key={index} className="player-row">
                                        <div className="player-primary">
                                            <img src={player.strThumb} alt={player.strPlayer} className="player-thumb" />
                                            <span className="player-name">{player.strPlayer}</span>
                                        </div>
                                        <div className="player-secondary">
                                            <span className="player-position">{player.strPosition}</span>
                                            <span className="player-number">#{player.intSquadNumber}</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="lineup-team">
                        <div className="lineup-header">
                            <img src={game.away_team_badge} alt={`${game.away_team} Badge`} className="team-badge" />
                            <h2>{game.away_team}</h2>
                        </div>
                        <div className="lineup-players">
                            {eventLineup.lineup
                                .filter(player => player.strTeam === game.away_team)
                                .map((player, index) => (
                                    <div key={index} className="player-row">
                                        <div className="player-primary">
                                            <img src={player.strThumb} alt={player.strPlayer} className="player-thumb" />
                                            <span className="player-name">{player.strPlayer}</span>
                                        </div>
                                        <div className="player-secondary">
                                            <span className="player-position">{player.strPosition}</span>
                                            <span className="player-number">#{player.intSquadNumber}</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Standings Section - for completed NBA games and all Premier League games (positioned last) */}
            {((isNBAGame && !isUpcoming) || isPLGame) && <StandingsSection />}

        </div>
    );
};

export default EventDetails;
